import $ from 'jquery'
import { h, Component, createRef } from 'preact';
import cx from 'classnames';
import moment from 'moment-timezone';
import { Subject, Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

import getStore, { appState$ } from '../store';

import {
  GET_LISTING_DATA,
  GET_LISTING_DETAIL,
  UPDATE_PROPERTY,
  CLEAR_FOCUSED_LISTING,
  REFRESH_LISTING,
  EXPIRE_LISTING,
} from '../reducers/listing/listing.actions';

import { HOPPLER_TIMEZONE, LayoutBreakpoints } from '../../../constants'
import { toDecimal } from '../../../core/decimal';
import { Form, FieldList, Field } from '../../utils/forms/form';
import { Permission } from '../../../types/permission.model';
import { Listing, REQUEST_SUCCESS, REQUEST_IN_PROGRESS, RequestState } from '../../../types';
import { ListingFilter } from '../../../types/listing-filter.model';
import * as Validators from '../../utils/forms/validators';
import hopplerLinkState, { hopplerResetForm } from '../../utils/forms/hoppler-linkstate';
import { options } from '../../../constants/options';
import { ListingEditor } from './listings/listing-editor.component';
import { CommentsModal } from './listings/comments-modal';
import { createKumitaListing } from '../../utils/create-api-input';
import { Formats } from '../../../constants/api';
import { debounce } from '../../utils/debounce';
import { DashListingsPaginator } from './listings/dash-listings-paginator.component';
import { Group } from '../../../types/group.model';
import { ListingFilterCard } from './listings/listing-filter-card.component';
import { ListingsGridSearchRow } from './listings/grid/listings-grid-search-row.component';

interface DashListingsProps {
  groupProfile?: string;
}

interface DashListingsState {
  currentStage?: string;
  gridData?: Listing[];
  currentGroupProfile?: string;
  isClearing: boolean;
  clearFilterButtonVisible: boolean;

  selectedFilterOption?: string;

  groups?: Group[];

  currentPage?: number;
  listingCount?: number;

  isDetailVisible?: boolean;
  isFilterVisible?: boolean;

  filterForm?: Form;

  isGettingListingDetail?: boolean;
  focusedListing?: Listing;
  updatePropertyStatus?: RequestState;
  refreshListingStatus?: RequestState;
  expireListingStatus?: RequestState;
  activeRow?: string
  reasonVisible?: boolean;
  expiryVisible?: boolean;

  commentsModalVisible?: boolean;

  propertyKey?: string;
  permissionComments: Permission;

  avePricePerSqm?: number;
  aveAreaSqm?: number;
  avePrice?: number;
  aveText?: string;
  ppsqmIsVisible?: boolean;
}

const PER_PAGE = 20;
const SORT_BY_OPTIONS = [
  { sortBy: 'asc', sort: "dateModified", label: 'Date Modified (Newest first)' },
  { sortBy: 'desc', sort: "dateModified", label: 'Date Modified (Oldest first)' },
  { name: 'divider' },

  { sortBy: 'asc', sort: "minPrice", label: 'Price (Low to High)' },
  { sortBy: 'desc', sort: "minPrice", label: 'Price (High to Low)' },
];

export class DashListings extends Component<DashListingsProps, DashListingsState> {
  detailListingRef = createRef()
  form$: Subject<Form>;
  private subscriptions: Subscription[] = [];
  getForm: Function;

  constructor(props: DashListingsProps) {
    super(props);

    this.state = {
      clearFilterButtonVisible: false,
      isClearing: false,
      activeRow: null,
      currentStage: 'available',
      commentsModalVisible: false,
      currentPage: 1,
      reasonVisible: false,
      currentGroupProfile: props.groupProfile,
      selectedFilterOption: "",
      propertyKey: "",
      permissionComments: {},
      avePricePerSqm: 0,
      aveAreaSqm: 0,
      avePrice: 0,
      aveText: '',
      ppsqmIsVisible: false
    };

    this.setupForm();
    this.toggleReasonMessage = this.toggleReasonMessage.bind(this)
    this.onRefreshListingClick = this.onRefreshListingClick.bind(this)
    this.onExpireListingClick = this.onExpireListingClick.bind(this)
    this.getListingSort = this.getListingSort.bind(this)
    this.openModal = this.openModal.bind(this)
  }

  hideCommentsModal = () => {
    this.setState({ commentsModalVisible: false })    
  }

  openModal(event, status: boolean, listing?: Listing) {
    event.stopPropagation();

    this.setState({
      commentsModalVisible: status
    })

    if (status == true) {
      this.setState({
        propertyKey: listing.propertyKey,
        permissionComments: listing.permissionComments
      })
    }
  }

  setupForm = () => {

    // FILTER PARAMETERS 
    let fields: FieldList = {
      'propertyKey': new Field([], null),
      'search': new Field([], null),
      'locationId': new Field([], null),
      'division': new Field([], null),
      'category': new Field([], null),
      'type': new Field([], null),
      'bedrooms': new Field([], null),
      'bathrooms': new Field([], null),

      'minPrice': new Field([], null),
      'maxPrice': new Field([], null),
      'minLotArea': new Field([], null),
      'maxLotArea': new Field([], null),
      'minFloorArea': new Field([], null),
      'maxFloorArea': new Field([], null),

      'alternate': new Field([], null), // Alternate contact
      'uploader': new Field([], null),
      'salesDirector': new Field([], null),
      'dateModifiedFrom': new Field([], null),
      'dateModifiedTo': new Field([], null),
      'dateExpiresFrom': new Field([], null),
      'dateExpiresTo': new Field([], null),
      'dateCreatedFrom': new Field([], null),
      'dateCreatedTo': new Field([], null),

      'source': new Field([], null, 'Any'),
    }

    this.form$ = new Subject<Form>();
    this.setState({
      filterForm: new Form(fields)
    });

    this.subscriptions.push(
      this.form$.pipe(distinctUntilChanged())
        .subscribe(form => {
          Validators.validateForm(form);
          this.setState({ filterForm: form });
        })
    );
  }

  onClearFiltersClicked = (): void => {

    this.setState({ isClearing: true })

    setTimeout(() => {
      this.setupForm();
      this.setState({
        isClearing: false,
        clearFilterButtonVisible: false,
        isFilterVisible: true
      })

      this.setState({ filterForm: hopplerResetForm(this.state.filterForm) })
      this.refresh();
    }, 500)

  }

  hasFilters = (): boolean => {
    const filters = [];

    const items = Object.entries(this.state.filterForm.fields)
    for (const [_, field] of items) {
      if (field.value !== undefined) {
        filters.push(_);
      }
    }

    if (filters.length == 0)
      return false;

    return true;
  }

  componentDidMount() {
    const listings$ = appState$.pipe(
      map(app => app.listing)
    );

    this.subscriptions.push(
      listings$.subscribe((listing) => {

        this.setState({
          refreshListingStatus: listing.refreshListingStatus,
          listingCount: listing.totalListings
        })

        if (listing.listings) {
          let pages: number[] = [];
          let numOfPages = Math.ceil(listing.totalListings / PER_PAGE);

          for (let i = 1; i <= numOfPages; i++) {
            pages.push(i);
          }

          listing.listings.forEach(listing => {
            const comments = listing.comments;
            if (!comments) {
              listing['comments'] = 0
            } else {
              listing['comments'] = comments
            }
          })

          this.setState({
            gridData: listing.listings
          });
        }

        if (listing.ppsqm) {
          this.setState({
            avePricePerSqm: listing.ppsqm.averagePricePerSqm,
            aveAreaSqm: listing.ppsqm.averageAreaSqm,
            avePrice: listing.ppsqm.averagePrice,
          })
        }
      })
    );

    this.subscriptions.push(
      listings$.pipe(
        map(listing => ({
          requestState: listing.getListingDetailState,
          focusedListing: listing.focusedListing
        })
        ),
        distinctUntilChanged()
      ).subscribe(({ requestState, focusedListing }) => {

        // state is for spinners
        if (requestState == REQUEST_IN_PROGRESS) {
          this.setState({
            isGettingListingDetail: true
          });
        } else {
          if (requestState == REQUEST_SUCCESS) {
            this.setState({
              focusedListing: focusedListing,
            });
          }

          this.setState({
            isGettingListingDetail: false
          })
        }

      })
    );

    this.subscriptions.push(
      listings$.pipe(
        map(listing => listing.updatePropertyStatus)
      ).subscribe((updatePropertyStatus) => {
        this.setState({
          updatePropertyStatus: updatePropertyStatus
        })
      })
    );

    this.subscriptions.push(appState$.pipe(
      map(appState => appState.user.currentGroups)
    ).subscribe(groups => this.setState({ groups })));

    this.refresh();

    // set filters
    this.setState({ selectedFilterOption: SORT_BY_OPTIONS[0].label });
  }

  updateListingFilter(sort: string, sortBy: string, label: string) {
    this.setState({
      selectedFilterOption: label
    })

    this.refresh({ sort, sortBy });
  }

  refresh(params?: { sort: string, sortBy: string }) {
    const filterObject: ListingFilter = {};
    const fields: FieldList = this.state.filterForm.fields;

    filterObject.pageSize = PER_PAGE;
    filterObject.page = this.state.currentPage;

    const locationId = fields['locationId'].value;
    const division = fields['division'].value;
    const category = fields['category'].value;
    const stage = this.state.currentStage;

    if (locationId && division && category) {
      this.setState({ ppsqmIsVisible: true });
    } else {
      this.setState({ ppsqmIsVisible: false });
    }

    if (stage == 'available') {
      filterObject.online = 1;
    } else {
      filterObject.online = 0;
    }

    if (fields['category'].value == 'Rent') {
      this.setState({ aveText: 'Rental' })
    } else {
      this.setState({ aveText: 'Sale' })
    }

    for (let k in fields) {
      if (fields[k].value) {
        if (k.startsWith('date')) {
          filterObject[k] = moment(fields[k].value).tz(HOPPLER_TIMEZONE).format();
        } else {
          filterObject[k] = fields[k].value;
        }
      }
    }

    if (filterObject.source === 'Any') {
      delete filterObject.source;
    }

    if (params) {
      filterObject.sort = params.sort;
      filterObject.sortBy = params.sortBy;
    }

    const store = getStore();
    store.dispatch({
      type: GET_LISTING_DATA,
      payload: {
        gridStage: this.state.currentStage,
        gridFilters: filterObject,
        groupProfile: this.state.currentGroupProfile
      }
    });
  }

  componentWillReceiveProps(nextProps: DashListingsProps) {
    if (nextProps.groupProfile != this.state.currentGroupProfile) {
      this.setState({
        currentGroupProfile: nextProps.groupProfile,
        currentPage: 1
      });

      this.refresh();
    }
  }

  onTabHeaderClicked = (stage: string) => {
    this.onListingBackClicked();
    this.setState({
      currentStage: stage,
      reasonVisible: false
    });

    this.refresh();
  }

  onSetFiltersClicked = () => {
    this.setState({
      isFilterVisible: !this.state.isFilterVisible,
      isDetailVisible: false,
      activeRow: null,
    });
  }

  onFilterSearchClicked = (e?: Event) => {
    if (e) {
      e.preventDefault();
    }

    const hasFilter = this.hasFilters();

    if (hasFilter) {
      this.setState({
        clearFilterButtonVisible: true
      })
    }

    this.setState({ currentPage: 1 });

    // On mobile, hide the filter card on search
    if (window.innerWidth < LayoutBreakpoints.lg.min) {
      this.setState({ isFilterVisible: false });
      // and then scroll to top manually, as the listings dash currently uses
      // page-wide scrolling.
      window.scrollTo(0, 0);
    }

    this.refresh();
  }

  onListingClicked = (listing: Listing) => {
    const { propertyKey } = listing;

    const store = getStore();
    store.dispatch({
      type: GET_LISTING_DETAIL,
      payload: {
        focusedPropertyKey: propertyKey
      }
    });

    this.setState({
      isDetailVisible: true,
      activeRow: propertyKey,
      isFilterVisible: false,
      reasonVisible: false
    });
  }

  onListingBackClicked = debounce(() => {
    const store = getStore();
    store.dispatch({
      type: CLEAR_FOCUSED_LISTING
    });

    this.setState({
      isDetailVisible: false,
      focusedListing: null,
      activeRow: null
    });
  }, 250)

  onPageClicked = (page: number) => {
    this.setState({
      currentPage: page
    });

    this.refresh();
  }

  onSave = () => {
    const formData: Form = this.getForm();
    const form = formData['form'];
    const currentLocation = formData['currentLocation'];
    const locationForm = formData['locationForm'];
    const multimedia = formData['multimedia'];

    let kumitaListing = createKumitaListing(
      form,
      this.state.focusedListing,
      currentLocation,
      locationForm,
      multimedia
    );

    const store = getStore();
    store.dispatch({
      type: UPDATE_PROPERTY,
      payload: {
        listingToUpdate: kumitaListing
      }
    });
  }

  onRefreshListingClick = async () => {

    const store = getStore();

    const listings$ = appState$.pipe(
      map(app => app.listing)
    );

    listings$
      .pipe(map(listing => listing))
      .subscribe((listing) => {
        this.setState({
          refreshListingStatus: listing.refreshListingStatus
        })
      })

    store.dispatch({
      type: REFRESH_LISTING,
      payload: {
        focusedPropertyKey: this.state.activeRow
      }
    });
  }

  onExpireListingClick = async () => {
    const store = getStore();

    const listings$ = appState$.pipe(
      map(app => app.listing)
    );

    listings$
      .pipe(map(listing => listing))
      .subscribe((listing) => {
        this.setState({
          expireListingStatus: listing.expireListingStatus
        })
      })

    store.dispatch({
      type: EXPIRE_LISTING,
      payload: {
        focusedPropertyKey: this.state.activeRow
      }
    });
  }

  setOnSave = (passedFunction: Function) => {
    this.getForm = passedFunction;
  }

  toggleReasonMessage() {
    const isVisible = this.state.reasonVisible
    this.setState({ reasonVisible: !isVisible })

    if (this.state.reasonVisible) {
      const ref = this.detailListingRef.current.classList[0]
      $(`.${ref}`).animate({ scrollTop: 0 }, 300)
    }
  }

  getBreadcrumbs(props: DashListingsProps, state: DashListingsState) {
    if (!state.groups) {
      return;
    }

    const group = state.groups.find(g => g.profile === props.groupProfile);
    let breadcrumbLabel: string;

    if (group) {
      if (group.profile === 'others') {
        breadcrumbLabel = `Other Teams' Listings`;
      } else {
        breadcrumbLabel = `${group.name} Group Listings`;
      }
    } else {
      breadcrumbLabel = 'Listings';
    }

    return (
      <nav aria-label="breadcrumb">
        {/* cookie crumbs */}
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/kumita-brokers">Dashboard</a></li>
          <li className="breadcrumb-item active" aria-current="page">{breadcrumbLabel}</li>
        </ol>
      </nav>
    );
  }

  getCardListContentBlock(props: DashListingsProps, state: DashListingsState) {
    if (state.gridData) {
      return (
        <div>
          <div className="hi-dash-listings__ppsqm-sm">
            {this.getPpsqmBlock(props, state)}
          </div>
          {
            !state.isDetailVisible ?
              <div className="hi-dash-listings__mobile-card-list d-md-none">
                {
                  state.gridData.map(row => {
                    return (
                      <div className="hi-dash-listings__mobile-card-list__card">

                        <div className="hi-dash-listings__mobile-card-list__card__details">
                          {this.getListingCardDetailsBlock(row)}
                        </div>

                        {/* <div className="hi-dash-listings__grid__col counter">
                        { this.getListingCounterBlock(row) }
                      </div> */}
                      </div>
                    );
                  })
                }

              </div> : null
          }
          {/* <div className="my-3 row">
            {this.getListingDetailBlock(props, state)}
          </div> */}
        </div>
      );
    }
  }

  getListingCardDetailsBlock(row: Listing) {
    const comments: number = row.comments;
    const perCom: Permission = row.permissionComments;

    return (
      <div className="row" onClick={e => this.onListingClicked(row)}>
        <div className="col-12 property-key">
          {row.propertyKey}
          <a href={`/${row.propertyKey}`} className="ml-2" target="_blank">
            <i className="fas fa-external-link-alt"></i>
          </a>
        </div>
        <p className="text-dark h6 font-weight-normal pl-3">({row.source})</p>
        <div className="col-12 type-cat">
          {row.type} for {row.category}
        </div>
        <div className="col-12 address">
          {row.address}
        </div>
        <div className="col-12 price">
          Php {row.minPrice ? toDecimal(row.minPrice) : ''}
          { row.category === 'Rent' ? '/month' : '' }
        </div>

        <ul className="col-12 list-unstyled other-details">
          <li><i className="fas fa-bed" /> { row.bedrooms } Beds</li>
          <li><i className="fas fa-bath" /> { row.bathrooms } Baths</li>

          { row.floorArea && (
            <li><i className="fas fa-expand" /> { row.floorArea } Sqm (Floor)</li>
          )}

          { row.lotArea && (
            <li><i className="fas fa-expand" /> { row.lotArea } Sqm (Lot)</li>
          )}

          <li>{ row.furnishing }</li>
        </ul>

        <div className="col-12">
          <hr className="w-100 my-2" />
        </div>

        <div className="d-flex justify-content-start views">
          <span className="mr-3">
            <i className="fas fa-eye mr-2"></i>
            {row.views} Views
          </span>
          <span className="mr-3">
            <i className="fas fa-envelope mr-2"></i>
            {row.leads} Inquiries
          </span>
          {
            perCom.view ? (
              <span className="mr-3" onClick={(e) => this.openModal(e, true, row)}>
                <i className="fas fa-comment-alt mr-1"></i>
                {comments > 0 ? comments : ''}
                &nbsp;
                {comments > 1 ? 'Comments' : 'Comment'}
              </span>
            ) : null
          }
        </div>
      </div>
    );
  }

  getFilterRow(props: DashListingsProps, state: DashListingsState) {
    let form = state.filterForm;

    return (
      <ListingsGridSearchRow 
        isFilterVisible={ state.isFilterVisible }
        searchValue={ form.fields.search.value }
        
        onSetFiltersClick={ this.onSetFiltersClicked }
        onSearchClick={ event => this.onFilterSearchClicked(event) }
        onSearchInput={ hopplerLinkState(this.form$, form, 'search') }/>
    );
  }

  getListingDetailBlock(props: DashListingsProps, state: DashListingsState) {

    if (state.isDetailVisible) {
      return (
        <div className="col-12 col-lg-6">
          {
            state.isGettingListingDetail ?
              <div className="w-100 d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
              :
              <div className="hi-dash-listings__details-block">
                <div className="hi-dash-listings__details-block__top container-fluid">

                  <div className="row hi-dash-listings__header h-100">

                    <div className="col-4 pl-3 d-flex">
                      <button
                        className="btn pl-1 pr-3"
                        onClick={this.onListingBackClicked}>
                        <i className="fas fa-arrow-left"></i>
                      </button>
                    </div>

                    <div className="col-4 hi-dash-listings__header__text px-0">
                      <h3 className="hi-dash-listings__filters-block__top__h3 mb-0">
                        Listing Details
                    </h3>
                    </div>

                    <div className={cx('col-4 pl-0 h-100 hi-dash-listings__buttonright', {
                      'd-none': state.updatePropertyStatus == REQUEST_IN_PROGRESS ||
                        state.refreshListingStatus == REQUEST_IN_PROGRESS
                    })}>

                      { state.focusedListing.canEdit ?
                        <div className="btn-group">
                          <button
                            onClick={this.onRefreshListingClick}
                            className="btn btn-default px-0 mr-2">
                            <span className="text-primary d-flex align-items-center">
                              <i className="mr-2 fas fa-sync-alt"></i>
                              <span className="button-label">Repost</span>
                            </span>
                          </button>

                          <button
                            onClick={this.onSave}
                            className="btn btn-default px-0">
                            <span className="text-primary d-flex align-items-center">
                              <i className="mr-2 fas fa-save"></i>
                              <span className="button-label">Save</span>
                            </span>
                          </button>
                        </div> : null
                      }

                    </div>

                    {
                      state.updatePropertyStatus == REQUEST_IN_PROGRESS ||
                        state.refreshListingStatus == REQUEST_IN_PROGRESS ?
                        <div className="col-4 d-flex align-items-center justify-content-end">
                          {this.getSpinnerBlock()}
                        </div> : null
                    }
                  </div>
                </div>

                <div
                  ref={this.detailListingRef}
                  className="hi-dash-listings__details-block__body container-fluid">
                  <ListingEditor
                    currentTab={this.state.currentStage}
                    setOnSave={this.setOnSave}
                    reasonVisibility={state.reasonVisible}
                    listing={state.focusedListing} />
                </div>
              </div>
          }
        </div>
      );
    }
  }

  getTabContentBlock(props: DashListingsProps, state: DashListingsState) {
    return (
      <div>
        {this.getFilterRow(props, state)}

        {this.getListingSort(props, state)}

        <div className="row">
          <div className={cx(
            {
              'col-12': !(state.isFilterVisible || state.isDetailVisible),
              'd-none d-lg-block col-6': state.isFilterVisible || state.isDetailVisible,
            }
          )}>
            { this.getGridBlock(props, state) }
            { this.getCardListContentBlock(props, state) }
          </div>

          {/* { this.getFiltersBlock(props, state) } */}

          <div className={cx('col-md-12 col-lg-6', {
            'd-none': !state.isFilterVisible
          })}>
            <ListingFilterCard
              clearing={this.state.isClearing}

              formSubject={this.form$}
              filterForm={this.state.filterForm}

              onFilterCloseClick={this.onSetFiltersClicked}
              onClearFiltersClicked={this.onClearFiltersClicked}
              onFilterSearchClicked={this.onFilterSearchClicked} />
          </div>

          {this.getListingDetailBlock(props, state)}
        </div>
      </div>
    );
  }

  getListingSort(props: DashListingsProps, state: DashListingsState) {
    const total: number = state.listingCount || 0;

    return (
      <div className="d-flex justify-content-between my-3">
        <div className="hi-dash-listings__filter-options">
          <p
            type="button"
            name="sortBy"
            data-toggle="dropdown"
            className="dropdown-toggle mb-0">
            Sort By
          <span className="font-weight-bold ml-1">{state.selectedFilterOption}</span>
          </p>
          <div className="dropdown-menu">
            {
              SORT_BY_OPTIONS.map(filter => {
                if (filter.hasOwnProperty('name')) {
                  return <div className="dropdown-divider"></div>
                } else {
                  return (
                    <div className="dropdown-item px-3">
                      <button
                        onClick={() => this.updateListingFilter(filter.sort, filter.sortBy, filter.label)}
                        className="btn">
                        {filter.label}
                      </button>
                    </div>
                  )
                }
              })
            }
          </div>

          <p className="text-muted ml-2 listing-count mb-0">({total.toLocaleString()} results)</p>
        </div>
      </div>
    )
  }

  getListingDetailsBlock(row: Listing) {
    let discounted = row.minPrice != row.discountedPrice;
    if (row.division == 'Commercial') {
      discounted = false;
    }
    if (!options.discountEnabled) {
      discounted = false;
    }
    return (
      <div className="row">
        {/*
          Temporarily disabled side-by-side on XL breakpoint to fit better onscreen
          without requiring side-scrolling.
        */}
        <div className="col-12 left">

          <div className="d-lg-none left__property-key">
            <div className="">
              <div>
                {row.propertyKey}
                <a
                  href={`/${row.propertyKey}`}
                  className="ml-2"
                  target="_blank">
                  <i className="fas fa-external-link-alt"></i>
                </a>
                <p className="text-dark h6 font-weight-normal">({row.source})</p>
              </div>
            </div>
          </div>

          <div className="left__type-cat">
            {row.type} for {row.category}
          </div>
          <div className="left__address">
            {row.address}
          </div>
          <div className="left__price">
            Php { discounted ? toDecimal(row.discountedPrice) : toDecimal(row.minPrice) }
            { row.category === 'Rent' ? '/month' : '' }
            { discounted ? <span style="text-decoration:line-through; padding-left: 1em"> { ' Php ' + toDecimal(row.minPrice) } { row.category === 'Rent' ? '/month' : '' } </span> : null }
          </div>
          <div className="d-flex mt-3">
            <p className="left__availability">
              Availability: {moment(row.dateAvailability).format(Formats.date)}
            </p>
            <p className="left__updated">
              Updated: { moment(row.dateModified).format(Formats.date) }
            </p>
          </div>

        </div>

        <div className="col-12 right">

          <div className="right__furnishing">
            {row.furnishing}
          </div>

          <div className="row">
          
            {
              row.type.toLowerCase() !== 'vacant lot' ? (
                <div className="col-md-5 right__bed-col">
                  <span className={ cx({
                    'd-none': row.division == 'Commercial'
                  }) }>
                    <i className="fas fa-bed fa-fw mr-2"></i>
                    {row.bedrooms} Beds
                  </span>
                  <span>
                    <i className="fas fa-bath fa-fw mr-2"></i>
                    {row.bathrooms}
                    {" "}
                    { row.division == 'Residential' ? 'Bathroom' : 'Restrooms' }
                  </span>
                </div>
              ) : null
            }

            <div className="col right__area-col">
              { row.floorArea ?
                <span>
                  <i className="fas fa-expand fa-fw mr-2"></i>
                  {row.floorArea.toLocaleString()} Sqm (Floor)
                </span> : null }

              { row.lotArea ?
                <span>
                  <i className="fas fa-expand fa-fw mr-2"></i>
                  {row.lotArea.toLocaleString()} Sqm (Lot)
                </span> : null }
            </div>

          </div>


     

        </div>

      </div>
    );
  }

  getListingCounterBlock(row: Listing) {

    const perCom: Permission = row.permissionComments;
    const comments: number = row.comments;

    return (
      <div className="text-left">
        <p className="mb-2">
          <i className="fas fa-eye mr-2"></i>
          {row.views} Views
        </p>
        <p className="mb-2">
          <i className="fas fa-envelope mr-2"></i>
          {row.leads} Inquiries
        </p>
        {
          perCom.view ? (
            <p
              className="mb-2"
              onClick={(e) => this.openModal(e, true, row)}>
              <i className="fas fa-comment-alt mr-2"></i>
              {comments > 0 ? comments : ''}
              {comments > 1 ? ' Comments' : ' Comment'}
            </p>
          ) : null
        }
      </div>
    );
  }

  getPpsqmBlock(props: DashListingsProps, state: DashListingsState) {

    let avePricePerSqm = 0;
    let avePrice = 0;

    if (state.avePricePerSqm)
      avePricePerSqm = parseFloat(state.avePricePerSqm.toFixed(2));

    if (state.avePrice)
      avePrice = parseFloat(state.avePrice.toFixed(2));

    if (state.ppsqmIsVisible) {
      return (
        <div className="details__ppsqm">

          <div className="text-center w-50">
            <p className="text-muted label">Avg. Price Per SQM</p>
            <p className="text-dark value">₱ {toDecimal(avePricePerSqm)}</p>
          </div>

          <div className="divider"></div>

          <div className="text-center w-50">
            <p className="text-muted label">Avg. {state.aveText} Price</p>
            <p className="text-dark value">₱ {toDecimal(avePrice)}</p>
          </div>
        </div>
      )
    } else {
      return (
        <div className="d-flex justify-content-center">
          <p className="mb-0 text-muted filter-message">
            To see average price per sqm, use filters and select at least
            location, property kind, and category
          </p>
        </div>
      )
    }
  }

  getGridBlock(props: DashListingsProps, state: DashListingsState) {

    if (state.gridData && state.gridData.length > 0) {
      return (
        <div className={cx(
          'hi-dash-listings__grid d-none d-md-block ',
          {
            'shrunk': state.isDetailVisible || state.isFilterVisible
          }
        )}>
          <div className="hi-dash-listings__grid__row headers">
            <div className="hi-dash-listings__grid__col d-none d-lg-block">
              Property ID
            </div>

            <div className="hi-dash-listings__grid__col details">
              <p className="mb-0">Property Details</p>
              {/* <p className="details-subtitle">
              To view ave. price/sqm and sale price, filter at least by location, property kind, category, and type
              </p> */}

              {this.getPpsqmBlock(props, state)}

            </div>

            <div className="hi-dash-listings__grid__col">
              Counter
            </div>

          </div>

          {
            state.gridData.map(row => {
              return (
                <div
                  className={cx('hi-dash-listings__grid__row', {
                    'pending': row.isPending,
                    'active_row': state.activeRow == row.propertyKey
                    })
                  }
                  onClick={e => this.onListingClicked(row)}>
                  <div className="hi-dash-listings__grid__col propertyKey d-none d-lg-block">
                    {row.propertyKey}
                    <a 
                      href={`/${row.propertyKey}`} 
                      target="_blank" 
                      className="ml-2"> 
                      <i className="fas fa-external-link-alt"></i> 
                    </a>
                    <p className="text-dark h6 font-weight-normal">({row.source})</p>
                  </div>

                  <div className={ cx('hi-dash-listings__grid__col details', {
                    'active_row': state.activeRow == row.propertyKey
                  }) }>
                    {this.getListingDetailsBlock(row)}
                  </div>

                  <div className={ cx('hi-dash-listings__grid__col counter', {
                    'active_row': state.activeRow == row.propertyKey
                  }) }>
                    {this.getListingCounterBlock(row)}
                  </div>

                </div>
              );
            })
          }

        </div>
      );
    } else {
      return (
        <div className="py-2 text-center">
          No listing data available
        </div>
      );
    }
  }

  getSpinnerBlock() {
    return (
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  getPagination(props: DashListingsProps, state: DashListingsState) {
    if (state.listingCount) {
      return (
        <div className={ cx({'d-none d-lg-block': state.isFilterVisible || state.isDetailVisible}) }>
          <DashListingsPaginator currentPage={state.currentPage}
            pageSize={PER_PAGE}
            totalItems={state.listingCount}
            onPageClicked={(page) => this.onPageClicked(page)} />
        </div>
      );
    }

    return null;
  }

  incrementCommentCount = (propertyKey: string) => {
    const listings: Listing[] = JSON.parse(JSON.stringify(this.state.gridData));

    const listing = listings.find(l => l.propertyKey == propertyKey)
    listing['comments'] += 1;
    this.setState({
      gridData: listings
    })
  }

  render(props: DashListingsProps, state: DashListingsState) {
    return (
      <div className="hi-kumita__dashboard hi-dash-listings">
        <div className="container-fluid">

          { this.state.commentsModalVisible ?
            <CommentsModal
              incrementCommentCount={ this.incrementCommentCount }
              type="listing"
              onClose={ this.hideCommentsModal }
              keycode={this.state.propertyKey}
              permissionComments={this.state.permissionComments}
            /> : null }

          {this.getBreadcrumbs(props, state)}

          <div className="row no-gutters mt-3">
            <div className="col-12 hi-dash-listings__tab-container">
              <div className="hi-dash-listings__tab-headers">
                <button onClick={e => this.onTabHeaderClicked('all')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'all' })}>
                  All
                </button>
                <button onClick={e => this.onTabHeaderClicked('available')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'available' })}>
                  Active
                </button>
                <button onClick={e => this.onTabHeaderClicked('occupied')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'occupied' })}>
                  Occupied
                </button>
                <button onClick={e => this.onTabHeaderClicked('pending')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'pending' })}>
                  Pending
                </button>
                <button onClick={e => this.onTabHeaderClicked('on-hold')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'on-hold' })}>
                  On-hold
                </button>
                <button onClick={e => this.onTabHeaderClicked('drafts')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'drafts' })}>
                  Drafts
                </button>
                <button onClick={e => this.onTabHeaderClicked('expired')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'expired' })}>
                  Expired
                </button>
                <button onClick={e => this.onTabHeaderClicked('declined')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'declined' })}>
                  Declined
                </button>
                <button onClick={e => this.onTabHeaderClicked('sold')}
                  className={cx('hi-dash-listings__tab-headers__tab',
                    { 'active': state.currentStage === 'sold' })}>
                  Sold
                </button>
              </div>

              <div className="hi-dash-listings__tab-content">
                {/* d-none d-md-block  */}
                <div className=""> 
                  {this.getTabContentBlock(props, state)}
                </div>

                <div className="hi-dash-listings__filter-sm-options d-none">
                  {this.getListingSort(props, state)}
                </div>

                {/* <div className="hi-dash-listings__ppsqm-sm">
                  {this.getPpsqmBlock(props, state)}
                </div> */}

                {/* <div className="d-md-none ">
                  {this.getCardListContentBlock(props, state)}
                </div> */}

                {this.getPagination(props, state)}

              </div>

            </div>
          </div>

        </div>
      </div>
    );
  }
}
